import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from "../api/client";
import { Heart, Upload, MapPin, User, Users, X, Video, Image as ImageIcon, Loader } from 'lucide-react';
import { AuthContext } from '../App';

// Backend origin used as prefix for media URLs returned as absolute paths
// (e.g. "/api/uploads/abc.jpg"). Defined here because the `<img src>` template
// below references BACKEND_URL directly.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';


// Reusable Media Section Component for Public/Private media
const MediaSection = ({ 
  title, 
  description, 
  photos, 
  videos, 
  maxPhotos, 
  maxVideos, 
  onFileChange,
  isPrivate = false,
  uploading = false,
  removeMedia
}) => {
  const photoCount = photos.length;
  const videoCount = videos.length;

  return (
    <section className={`rounded-xl border ${
      isPrivate ? 'border-amber-500/40 bg-amber-950/20' : 'border-gray-700 bg-gray-900/40'
    } p-4 space-y-3`}>
      <div className="flex items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-white flex items-center gap-2">
            {isPrivate && <span className="text-amber-400">🔒</span>}
            {title}
          </h3>
          <p className="text-xs text-gray-400 mt-1">{description}</p>
        </div>
        <div className="text-xs text-gray-300 text-right space-y-1">
          <div>{photoCount}/{maxPhotos} photos</div>
          <div>{videoCount}/{maxVideos} videos</div>
        </div>
      </div>

      {/* Upload button */}
      <div className="flex flex-wrap gap-2">
        <label className={`text-xs px-3 py-1.5 rounded-full cursor-pointer transition-colors ${
          photoCount >= maxPhotos || uploading
            ? 'bg-gray-600 text-gray-400 cursor-not-allowed' 
            : 'bg-gray-800 hover:bg-gray-700 text-white border border-gray-600'
        }`}>
          {uploading ? 'Uploading...' : photoCount >= maxPhotos ? 'Photo limit reached' : '📸 Upload Photo'}
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => onFileChange(e, isPrivate)}
            disabled={photoCount >= maxPhotos || uploading}
          />
        </label>
        
        <label className={`text-xs px-3 py-1.5 rounded-full cursor-pointer transition-colors ${
          videoCount >= maxVideos || uploading
            ? 'bg-gray-600 text-gray-400 cursor-not-allowed' 
            : 'bg-gray-800 hover:bg-gray-700 text-white border border-gray-600'
        }`}>
          {uploading ? 'Uploading...' : videoCount >= maxVideos ? 'Video limit reached' : '🎥 Upload Video'}
          <input
            type="file"
            accept="video/*"
            className="hidden"
            onChange={(e) => onFileChange(e, isPrivate)}
            disabled={videoCount >= maxVideos || uploading}
          />
        </label>
      </div>

      {/* Preview grid */}
      {(photoCount > 0 || videoCount > 0) && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {photos.map((url, i) => (
            <div key={`photo-${i}`} className="relative h-20 w-full rounded-md overflow-hidden bg-gray-800 group">
              <img src={`${BACKEND_URL}${url}`} alt="" className="h-full w-full object-cover" />
              {isPrivate && (
                <div className="absolute top-1 right-1 text-[10px] bg-black/70 px-1.5 py-0.5 rounded text-amber-300 font-semibold">
                  🔒 Private
                </div>
              )}
              <button
                type="button"
                onClick={() => removeMedia(isPrivate ? 'private_photos' : 'photos', url)}
                className="absolute top-1 left-1 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
          {videos.map((url, i) => (
            <div key={`video-${i}`} className="relative h-20 w-full rounded-md overflow-hidden bg-gray-800 group flex items-center justify-center">
              <Video className="w-6 h-6 text-gray-400" />
              <span className="absolute text-[10px] text-gray-200">Video</span>
              {isPrivate && (
                <div className="absolute top-1 right-1 text-[10px] bg-black/70 px-1.5 py-0.5 rounded text-amber-300 font-semibold">
                  🔒 Private
                </div>
              )}
              <button
                type="button"
                onClick={() => removeMedia(isPrivate ? 'private_videos' : 'videos', url)}
                className="absolute top-1 left-1 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {photoCount === 0 && videoCount === 0 && (
        <div className="border-2 border-dashed border-gray-600 rounded-lg p-4 text-center">
          <p className="text-xs text-gray-500">No {isPrivate ? 'private' : 'public'} media uploaded yet</p>
        </div>
      )}
    </section>
  );
};

const ProfileSetup = () => {
  const { user, token } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    names: user?.user_type === 'couple' ? ['', ''] : [''],
    ages: user?.user_type === 'couple' ? ['', ''] : [''],
    location: '',
    background: '',
    bio: '',
    preferences: [],
    non_negotiables: [],
    photos: [],
    videos: [],
    private_photos: [],
    private_videos: [],
    interests: []
  });
  
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [error, setError] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [profileId, setProfileId] = useState(null);

  // Load existing profile on mount
  React.useEffect(() => {
    const loadExistingProfile = async () => {
      try {
        const response = await apiClient.get(`/profiles/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data) {
          // Profile exists - switch to edit mode
          setIsEditMode(true);
          setProfileId(response.data.id);
          setFormData({
            names: response.data.names || (user?.user_type === 'couple' ? ['', ''] : ['']),
            ages: response.data.ages || (user?.user_type === 'couple' ? ['', ''] : ['']),
            location: response.data.location || '',
            background: response.data.background || '',
            bio: response.data.bio || '',
            preferences: response.data.preferences || [],
            non_negotiables: response.data.non_negotiables || [],
            photos: response.data.photos || [],
            videos: response.data.videos || [],
            private_photos: response.data.private_photos || [],
            private_videos: response.data.private_videos || [],
            interests: response.data.interests || []
          });
        }
      } catch (error) {
        // 404 means no profile exists yet - stay in create mode
        if (error.response?.status !== 404) {
          console.error('Failed to load profile:', error);
        }
      }
    };

    loadExistingProfile();
  }, [token, user]);

  const backgroundOptions = [
    'African American',
    'Afrolatino - Mexican',
    'Afrolatino - Puerto Rican',
    'Afrolatino - Dominican',
    'Afrolatino - Colombian',
    'Afrolatino - Cuban',
    'Afrolatino - Brazilian',
    'Afrolatino - Honduran',
    'Afrolatino - Guatemalan',
    'Afrolatino - Other',
    'Nigerian',
    'Ghanaian',
    'Ethiopian',
    'Kenyan',
    'South African',
    'Jamaican',
    'Haitian',
    'Caribbean - Trinidad & Tobago',
    'Caribbean - Barbados',
    'Caribbean - Other',
    'Mixed Heritage - Black/Latino',
    'Mixed Heritage - Other',
    'Other'
  ];

  const interestOptions = [
    'Travel', 'Music', 'Dancing', 'Cooking', 'Fitness', 'Art', 'Reading',
    'Movies', 'Fashion', 'Photography', 'Yoga', 'Hiking', 'Business',
    'Spirituality', 'Family Time', 'Cultural Events', 'Social Causes'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleArrayInputChange = (field, index, value) => {
    const newArray = [...formData[field]];
    newArray[index] = value;
    setFormData({ ...formData, [field]: newArray });
  };

  // Determine profile setup based on user type
  const isCouple = user?.user_type === 'couple';
  const isSingle = user?.user_type === 'single';
  const isPolySingle = user?.user_type === 'poly_single';

  const getProfileTitle = () => {
    const action = isEditMode ? 'Edit' : 'Create';
    if (isCouple) return `${action} Your Couple Profile`;
    if (isSingle) return `${action} Your Profile`;
    if (isPolySingle) return `${action} Your Poly Profile`;
    return `${action} Your Profile`;
  };

  const getProfileDescription = () => {
    if (isCouple) return 'Tell others about your relationship and what you\'re seeking together';
    if (isSingle) return 'Tell couples about yourself and what you\'re looking for';
    if (isPolySingle) return 'Tell others about yourself and your poly journey goals';
    return 'Tell us about yourself to find your perfect match';
  };

  const toggleArrayItem = (field, item) => {
    const newArray = formData[field].includes(item)
      ? formData[field].filter(i => i !== item)
      : [...formData[field], item];
    setFormData({ ...formData, [field]: newArray });
  };

  const handleFileUpload = async (e, isPrivate = false) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    setError('');

    // Get current counts
    const publicPhotos = formData.photos.length;
    const publicVideos = formData.videos.length;
    const privatePhotos = formData.private_photos.length;
    const privateVideos = formData.private_videos.length;

    try {
      const uploadPromises = files.map(async (file, index) => {
        // Check file type
        const isVideo = file.type.startsWith('video/');
        const isImage = file.type.startsWith('image/');

        if (!isVideo && !isImage) {
          throw new Error(`File "${file.name}" is not a valid image or video`);
        }

        // Check file size (50MB max)
        if (file.size > 50 * 1024 * 1024) {
          throw new Error(`File "${file.name}" exceeds 50MB limit`);
        }

        // Create form data
        const formDataObj = new FormData();
        formDataObj.append('file', file);

        // Track upload progress
        const fileId = `${Date.now()}-${index}`;
        setUploadProgress(prev => ({ ...prev, [fileId]: 0 }));

        // Upload file
        const response = await apiClient.post(`/upload`, formDataObj, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(prev => ({ ...prev, [fileId]: progress }));
          }
        });

        // Remove progress tracking
        setUploadProgress(prev => {
          const newProgress = { ...prev };
          delete newProgress[fileId];
          return newProgress;
        });

        return {
          url: response.data.file_url,
          type: response.data.file_type
        };
      });

      const uploadedFiles = await Promise.all(uploadPromises);

      // Add uploaded files to formData based on visibility
      const newPhotos = uploadedFiles.filter(f => f.type === 'photo').map(f => f.url);
      const newVideos = uploadedFiles.filter(f => f.type === 'video').map(f => f.url);

      if (isPrivate) {
        // Check limits for private media
        if (privatePhotos + newPhotos.length > 3) {
          throw new Error('Maximum 3 private photos allowed');
        }
        if (privateVideos + newVideos.length > 1) {
          throw new Error('Maximum 1 private video allowed');
        }

        setFormData({
          ...formData,
          private_photos: [...formData.private_photos, ...newPhotos],
          private_videos: [...formData.private_videos, ...newVideos]
        });
      } else {
        // Check limits for public media
        if (publicPhotos + newPhotos.length > 6) {
          throw new Error('Maximum 6 public photos allowed');
        }
        if (publicVideos + newVideos.length > 2) {
          throw new Error('Maximum 2 public videos allowed');
        }

        setFormData({
          ...formData,
          photos: [...formData.photos, ...newPhotos],
          videos: [...formData.videos, ...newVideos]
        });
      }

    } catch (error) {
      console.error('File upload error:', error);
      setError(error.message || 'Failed to upload files. Please try again.');
    } finally {
      setUploading(false);
      // Reset file input
      e.target.value = '';
    }
  };

  const removeMedia = (type, url) => {
    setFormData({
      ...formData,
      [type]: formData[type].filter(item => item !== url)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Convert ages to numbers
      const processedData = {
        ...formData,
        ages: formData.ages.map(age => parseInt(age))
      };

      if (isEditMode) {
        // Update existing profile
        await apiClient.put(`/profiles/me`, processedData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        // Create new profile
        await apiClient.post(`/profiles`, processedData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      // If user came from an invite flow (or other deep link), return there after setup.
      try {
        const raw = localStorage.getItem('post_profile_setup_redirect');
        if (raw && typeof raw === 'string' && raw.startsWith('/') && !raw.startsWith('//')) {
          localStorage.removeItem('post_profile_setup_redirect');
          navigate(raw, { replace: true });
          return;
        }
      } catch (e) {}

      navigate('/dashboard');
    } catch (error) {
      console.error('Profile save error:', error);
      setError(
        error.response?.data?.detail || 
        `Failed to ${isEditMode ? 'update' : 'create'} profile. Please try again.`
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-warm py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Heart className="w-8 h-8 text-rose-600 fill-current" />
            <span className="font-playfair text-2xl font-bold text-gray-900">
              Unity Love
            </span>
          </div>
          <h1 className="font-playfair text-4xl font-bold text-gray-900 mb-2">
            {getProfileTitle()}
          </h1>
          <p className="text-xl text-gray-600">
            {getProfileDescription()}
          </p>
        </div>

        <div className="glass rounded-2xl p-8 fade-in">
          <form onSubmit={handleSubmit} className="space-y-8">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
                {error}
              </div>
            )}

            {/* Names and Ages */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="form-label">
                  {user?.user_type === 'couple' ? 'Partner Names' : 'Your Name'}
                </label>
                {formData.names.map((name, index) => (
                  <input
                    key={index}
                    type="text"
                    value={name}
                    onChange={(e) => handleArrayInputChange('names', index, e.target.value)}
                    className={`form-input ${index > 0 ? 'mt-2' : ''}`}
                    placeholder={user?.user_type === 'couple' ? `Partner ${index + 1} name` : 'Your name'}
                    required
                    data-testid={`name-input-${index}`}
                  />
                ))}
              </div>

              <div>
                <label className="form-label">
                  {user?.user_type === 'couple' ? 'Ages' : 'Your Age'}
                </label>
                {formData.ages.map((age, index) => (
                  <input
                    key={index}
                    type="number"
                    min="18"
                    max="100"
                    value={age}
                    onChange={(e) => handleArrayInputChange('ages', index, e.target.value)}
                    className={`form-input ${index > 0 ? 'mt-2' : ''}`}
                    placeholder={user?.user_type === 'couple' ? `Partner ${index + 1} age` : 'Your age'}
                    required
                    data-testid={`age-input-${index}`}
                  />
                ))}
              </div>
            </div>

            {/* Location and Background */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="location" className="form-label">
                  <MapPin className="inline w-4 h-4 mr-1" />
                  Location
                </label>
                <input
                  type="text"
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="City, State"
                  required
                  data-testid="location-input"
                />
              </div>

              <div>
                <label htmlFor="background" className="form-label">
                  Cultural Background
                </label>
                <select
                  id="background"
                  name="background"
                  value={formData.background}
                  onChange={handleInputChange}
                  className="form-input"
                  required
                  data-testid="background-select"
                >
                  <option value="">Select your background</option>
                  {backgroundOptions.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Bio */}
            <div>
              <label htmlFor="bio" className="form-label">
                About {user?.user_type === 'couple' ? 'Us' : 'Me'}
              </label>
              <textarea
                id="bio"
                name="bio"
                value={formData.bio}
                onChange={handleInputChange}
                rows="4"
                className="form-input resize-none"
                placeholder={`Tell ${user?.user_type === 'couple' ? 'others' : 'us'} about ${user?.user_type === 'couple' ? 'yourselves' : 'yourself'}...`}
                required
                data-testid="bio-textarea"
              />
            </div>

            {/* Interests */}
            <div>
              <label className="form-label">Interests & Hobbies</label>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {interestOptions.map((interest) => (
                  <button
                    key={interest}
                    type="button"
                    onClick={() => toggleArrayItem('interests', interest)}
                    className={`p-3 text-sm rounded-xl border-2 transition-all ${
                      formData.interests.includes(interest)
                        ? 'border-rose-500 bg-rose-50 text-rose-700'
                        : 'border-gray-200 hover:border-rose-300 text-gray-600'
                    }`}
                    data-testid={`interest-${interest.toLowerCase().replace(' ', '-')}`}
                  >
                    {interest}
                  </button>
                ))}
              </div>
            </div>

            {/* Preferences */}
            <div>
              <label className="form-label">What you're looking for</label>
              <textarea
                value={formData.preferences.join(', ')}
                onChange={(e) => setFormData({
                  ...formData,
                  preferences: e.target.value.split(', ').filter(p => p.trim())
                })}
                rows="3"
                className="form-input resize-none"
                placeholder="e.g., Adventurous spirit, Family-oriented, Career-driven..."
                data-testid="preferences-textarea"
              />
            </div>

            {/* Non-negotiables */}
            <div>
              <label className="form-label">Deal Breakers</label>
              <textarea
                value={formData.non_negotiables.join(', ')}
                onChange={(e) => setFormData({
                  ...formData,
                  non_negotiables: e.target.value.split(', ').filter(p => p.trim())
                })}
                rows="2"
                className="form-input resize-none"
                placeholder="e.g., Smoking, Dishonesty, Different life goals..."
                data-testid="non-negotiables-textarea"
              />
            </div>

            {/* Public & Private Media Sections */}
            <div className="space-y-4">
              {/* Upload Progress (shared between both sections) */}
              {Object.keys(uploadProgress).length > 0 && (
                <div className="mb-4 space-y-2">
                  {Object.entries(uploadProgress).map(([fileId, progress]) => (
                    <div key={fileId} className="bg-gray-100 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-gray-600">Uploading...</span>
                        <span className="text-sm font-semibold text-rose-600">{progress}%</span>
                      </div>
                      <div className="w-full bg-gray-300 rounded-full h-2">
                        <div
                          className="bg-rose-600 h-2 rounded-full transition-all"
                          style={{ width: `${progress}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* PUBLIC MEDIA */}
              <MediaSection
                title="Public Photos & Videos"
                description="Visible on your profile to everyone on Unity Love."
                photos={formData.photos}
                videos={formData.videos}
                maxPhotos={6}
                maxVideos={2}
                onFileChange={handleFileUpload}
                isPrivate={false}
                uploading={uploading}
                removeMedia={removeMedia}
              />

              {/* PRIVATE MEDIA */}
              <MediaSection
                title="Private Photos & Videos"
                description="Only visible to people you explicitly share them with."
                photos={formData.private_photos}
                videos={formData.private_videos}
                maxPhotos={3}
                maxVideos={1}
                onFileChange={handleFileUpload}
                isPrivate={true}
                uploading={uploading}
                removeMedia={removeMedia}
              />

              <p className="text-xs text-gray-500 mt-2">
                Max 50MB per file. Supported: Images (JPG, PNG, GIF, WebP) and Videos (MP4, WebM, MOV)
              </p>
            </div>

            {/* Submit Button */}
            <div className="flex justify-center">
              <button
                type="submit"
                disabled={loading}
                className="btn-primary px-12 py-3 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="submit-profile-btn"
              >
                {loading ? (
                  <div className="flex items-center space-x-2">
                    <div className="spinner w-4 h-4 border-2"></div>
                    <span>{isEditMode ? 'Saving Changes...' : 'Creating Profile...'}</span>
                  </div>
                ) : (
                  isEditMode ? 'Save Changes' : 'Create Profile'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ProfileSetup;